import { useState, useRef, useEffect } from 'react';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../firebase';
import { loginPhone, registerPhone, resetPasswordPhone, loginAnonymously } from '../api';

export default function Login({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // steps: 'login' | 'signup-phone' | 'signup-otp' | 'signup-password'
  //        'otp-login-phone' | 'otp-login-otp' | 'reset-password'
  const [step, setStep] = useState('login');

  // Login fields
  const [loginPhone_, setLoginPhone] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [failCount, setFailCount] = useState(0);

  // Shared OTP/signup fields
  const [signupPhone, setSignupPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const recaptchaVerifier = useRef(null);
  const confirmationResult = useRef(null);
  const verifiedIdToken = useRef(null);

  // Cleanup verifier when leaving OTP steps
  useEffect(() => {
    if (['signup-phone', 'signup-otp', 'otp-login-phone', 'otp-login-otp'].includes(step)) return;
    if (recaptchaVerifier.current) {
      try { recaptchaVerifier.current.clear(); } catch {}
      recaptchaVerifier.current = null;
    }
  }, [step]);

  const startResendTimer = () => {
    setResendTimer(30);
    const iv = setInterval(() => {
      setResendTimer(t => {
        if (t <= 1) { clearInterval(iv); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  // ── Login with password ────────────────────────────────────
  const handleLogin = async () => {
    const phone = loginPhone_.replace(/\D/g, '');
    if (phone.length < 10) return setError('Enter a valid 10-digit phone number.');
    if (!loginPassword) return setError('Enter your password.');
    setLoading(true);
    setError('');
    try {
      const result = await loginPhone(`+91${phone}`, loginPassword);
      if (onLogin && result?.user) onLogin(result.user);
      else window.location.href = '/';
    } catch (err) {
      const newFail = failCount + 1;
      setFailCount(newFail);
      setError(err.message || 'Invalid phone number or password.');
    } finally {
      setLoading(false);
    }
  };

  // ── Send OTP (used for both signup and forgot-password) ────
  const handleSendOtp = async () => {
    if (signupPhone.length < 10) return setError('Enter a valid 10-digit phone number.');
    setLoading(true);
    setError('');
    try {
      if (recaptchaVerifier.current) {
        try { recaptchaVerifier.current.clear(); } catch {}
      }
      const container = document.getElementById('recaptcha-container');
      if (container) container.innerHTML = '';
      recaptchaVerifier.current = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
      const result = await signInWithPhoneNumber(auth, `+91${signupPhone.trim()}`, recaptchaVerifier.current);
      confirmationResult.current = result;
      setStep(step === 'otp-login-phone' ? 'otp-login-otp' : 'signup-otp');
      startResendTimer();
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/invalid-phone-number') setError('Invalid phone number.');
      else if (code === 'auth/too-many-requests') setError('Too many requests. Please wait a while and try again.');
      else if (code === 'auth/network-request-failed') setError('Network error. Check your connection.');
      else setError(`Failed to send OTP. [${code}]`);
    } finally {
      setLoading(false);
    }
  };

  // ── Verify OTP (signup → set password / forgot → reset password) ─
  const handleVerifyOtp = async () => {
    if (otp.length < 6) return setError('Enter the 6-digit OTP.');
    if (!confirmationResult.current) {
      setError('Session expired. Please request a new OTP.');
      setStep(step === 'otp-login-otp' ? 'otp-login-phone' : 'signup-phone');
      setOtp('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const credential = await confirmationResult.current.confirm(otp);
      verifiedIdToken.current = await credential.user.getIdToken();
      setStep(step === 'otp-login-otp' ? 'reset-password' : 'signup-password');
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/invalid-verification-code' || code === 'auth/code-expired') {
        setError('Incorrect OTP. Please try again.');
        setOtp('');
      } else if (code === 'auth/session-expired') {
        setError('OTP expired. Please request a new one.');
        setStep(step === 'otp-login-otp' ? 'otp-login-phone' : 'signup-phone');
        setOtp('');
      } else {
        setError(err.message || 'OTP verification failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Register new user ──────────────────────────────────────
  const handleRegister = async () => {
    if (password.length < 6) return setError('Password must be at least 6 characters.');
    if (password !== confirmPassword) return setError('Passwords do not match.');
    setLoading(true);
    setError('');
    try {
      const result = await registerPhone(verifiedIdToken.current, password);
      if (onLogin && result?.user) onLogin(result.user, true);
      else window.location.href = '/';
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Reset password for existing user ──────────────────────
  const handleResetPassword = async () => {
    if (password.length < 6) return setError('Password must be at least 6 characters.');
    if (password !== confirmPassword) return setError('Passwords do not match.');
    setLoading(true);
    setError('');
    try {
      const result = await resetPasswordPhone(verifiedIdToken.current, password);
      if (onLogin && result?.user) onLogin(result.user);
      else window.location.href = '/';
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnonymousLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await loginAnonymously();
      if (onLogin && result?.user) onLogin(result.user);
      else window.location.href = '/';
    } catch {
      setError('Guest login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goToSignup = () => { setError(''); setStep('signup-phone'); };
  const goToLogin  = () => {
    setError(''); setStep('login');
    setOtp(''); setSignupPhone(''); setPassword(''); setConfirmPassword('');
  };
  const goToOtpLogin = () => {
    setError(''); setOtp(''); setPassword(''); setConfirmPassword('');
    // Pre-fill phone from login field so user doesn't retype
    if (loginPhone_.length >= 10) setSignupPhone(loginPhone_);
    setStep('otp-login-phone');
  };

  const isOtpStep = step === 'signup-otp' || step === 'otp-login-otp';
  const prevOtpStep = step === 'otp-login-otp' ? 'otp-login-phone' : 'signup-phone';

  return (
    <div data-theme="light" style={{ minHeight: '100vh', display: 'flex', background: '#f7f8fc', color: '#0f172a' }}>
      <div id="recaptcha-container" />

      {/* Left branding panel */}
      <div className="login-left-panel" style={{
        flex: 1,
        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 50%, #075985 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: 48, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -80, right: -80, width: 320, height: 320, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
        <div style={{ position: 'absolute', top: '40%', left: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

        <img
          src="/logo-mascot.png"
          alt="HireKahani"
          style={{ width: 260, height: 260, objectFit: 'contain', zIndex: 1, filter: 'drop-shadow(0 24px 48px rgba(0,0,0,0.25))' }}
        />
        <div style={{ zIndex: 1, color: '#fff', textAlign: 'center', marginTop: 24, maxWidth: 400 }}>
          <div style={{ fontFamily: 'var(--display)', fontWeight: 900, fontSize: 28, letterSpacing: -1, marginBottom: 14 }}>
            Welcome to Hire <span style={{ color: '#000' }}>Kahani</span>
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.8, opacity: 0.9, marginBottom: 16 }}>
            Read honest company reviews, interview experiences, and job market insights shared by real people. Vent your frustrations anonymously, warn others, and make smarter career decisions — all in one place.
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>
            Real stories. Real anonymity. Real control.
          </div>
        </div>

      </div>

      {/* Right panel */}
      <div className="login-right-panel" style={{
        width: 520, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: 48, background: '#f7f8fc',
      }}>
        <div style={{ width: '100%', maxWidth: 380 }}>

          {/* Logo */}
          <div className="login-logo-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 32 }}>
            <img src="/logo-mascot.png" alt="HireKahani" className="login-mobile-logo" style={{ display: 'none', width: 40, height: 40, objectFit: 'contain' }} />
            <div style={{ fontFamily: 'var(--display)', fontWeight: 900, fontSize: 36, letterSpacing: -1 }}>
              <span style={{ color: 'var(--blue)' }}>Hire</span><span style={{ color: '#000000' }}>Kahani</span>
            </div>
          </div>

          {/* Error box */}
          {error && (
            <div style={{ background: '#fff1f2', color: '#e11d48', padding: '12px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, marginBottom: 16, border: '1px solid #fda4af' }}>
              {error}
            </div>
          )}


          {/* ── STEP: Login ── */}
          {step === 'login' && (
            <>
              <h1 style={{ fontFamily: 'var(--display)', fontSize: 26, fontWeight: 700, margin: '0 0 6px', color: '#0f172a' }}>
                Welcome
              </h1>
              <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 24px', lineHeight: 1.6 }}>
                Sign in with your phone number and password.
              </p>

              {/* Phone field */}
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Phone Number</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 700, color: '#64748b', pointerEvents: 'none' }}>+91</div>
                  <input
                    autoFocus
                    type="tel"
                    value={loginPhone_}
                    onChange={e => setLoginPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    onKeyDown={e => e.key === 'Enter' && handleLogin()}
                    placeholder="10-digit mobile number"
                    style={{ ...inputStyle, paddingLeft: 44 }}
                  />
                </div>
              </div>

              {/* Password field */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showLoginPwd ? 'text' : 'password'}
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleLogin()}
                    placeholder="Your password"
                    style={{ ...inputStyle, paddingRight: 44 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPwd(v => !v)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#94a3b8', display: 'flex', alignItems: 'center' }}
                  >
                    <EyeIcon open={showLoginPwd} />
                  </button>
                </div>
              </div>

              <button
                onClick={handleLogin}
                disabled={loading}
                style={{
                  width: '100%', background: 'var(--blue)', color: '#fff',
                  padding: '12px', borderRadius: 8, fontSize: 14, fontWeight: 700,
                  border: 'none', cursor: loading ? 'default' : 'pointer',
                  boxShadow: '0 4px 12px rgba(14,165,233,0.3)', marginBottom: 20,
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Signing in...' : 'Login'}
              </button>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>New user?</span>
                  <button
                    onClick={goToSignup}
                    style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                  >
                    Sign Up
                  </button>
                </div>
                <button
                  onClick={goToOtpLogin}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >
                  Forgot password?
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 14px' }}>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>OR</span>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              </div>

              <button
                onClick={handleAnonymousLogin}
                disabled={loading}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', padding: '11px', borderRadius: 8,
                  border: '1.5px solid #e2e8f0', background: '#fff',
                  fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 600, color: '#64748b',
                  cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <span style={{ fontSize: 16 }}>👤</span>
                Continue as Guest
              </button>
            </>
          )}

          {/* ── STEP: Signup — Enter phone ── */}
          {step === 'signup-phone' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <button onClick={goToLogin} style={backBtn}>←</button>
                <h1 style={stepTitle}>Create account</h1>
              </div>
              <p style={stepSubtitle}>Enter your mobile number. We'll send a verification OTP.</p>
              <PhoneInput value={signupPhone} onChange={setSignupPhone} onEnter={handleSendOtp} />
              <PrimaryButton onClick={handleSendOtp} disabled={loading || signupPhone.length < 10} active={signupPhone.length >= 10}>
                {loading ? 'Sending...' : 'Send OTP'}
              </PrimaryButton>
            </>
          )}

          {/* ── STEP: Forgot password — Enter phone ── */}
          {step === 'otp-login-phone' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <button onClick={goToLogin} style={backBtn}>←</button>
                <h1 style={stepTitle}>Verify your number</h1>
              </div>
              <p style={stepSubtitle}>We'll send an OTP to confirm it's you, then you can set a new password.</p>
              <PhoneInput value={signupPhone} onChange={setSignupPhone} onEnter={handleSendOtp} />
              <PrimaryButton onClick={handleSendOtp} disabled={loading || signupPhone.length < 10} active={signupPhone.length >= 10}>
                {loading ? 'Sending...' : 'Send OTP'}
              </PrimaryButton>
            </>
          )}

          {/* ── STEP: OTP input (shared for signup & forgot-password) ── */}
          {isOtpStep && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <button onClick={() => { setStep(prevOtpStep); setOtp(''); setError(''); }} style={backBtn}>←</button>
                <h1 style={stepTitle}>{step === 'otp-login-otp' ? 'Verify OTP' : 'Verify OTP'}</h1>
              </div>
              <p style={stepSubtitle}>
                OTP sent to <strong>+91 {signupPhone}</strong>. Enter it below.
              </p>
              <OtpBoxes otp={otp} setOtp={setOtp} />
              <PrimaryButton onClick={handleVerifyOtp} disabled={loading || otp.length < 6} active={otp.length >= 6} style={{ marginBottom: 12 }}>
                {loading ? 'Verifying...' : 'Verify OTP'}
              </PrimaryButton>
              <div style={{ textAlign: 'center' }}>
                {resendTimer > 0 ? (
                  <span style={{ fontSize: 13, color: '#94a3b8' }}>Resend OTP in {resendTimer}s</span>
                ) : (
                  <button
                    onClick={() => { setOtp(''); setStep(prevOtpStep); }}
                    style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </>
          )}

          {/* ── STEP: Signup — Set Password ── */}
          {step === 'signup-password' && (
            <>
              <h1 style={{ ...stepTitle, marginBottom: 6 }}>Set Password</h1>
              <p style={stepSubtitle}>Phone verified ✓ — Choose a password for your account.</p>
              <PasswordFields
                password={password} setPassword={setPassword}
                confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
                showPwd={showPwd} setShowPwd={setShowPwd}
                onEnter={handleRegister}
              />
              <PrimaryButton onClick={handleRegister} disabled={loading || password.length < 6 || password !== confirmPassword} active={password.length >= 6 && password === confirmPassword}>
                {loading ? 'Creating account...' : 'Create Account'}
              </PrimaryButton>
            </>
          )}

          {/* ── STEP: Forgot Password — Reset Password ── */}
          {step === 'reset-password' && (
            <>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔐</div>
                <h1 style={{ ...stepTitle, marginBottom: 4 }}>Set New Password</h1>
                <p style={stepSubtitle}>OTP verified ✓ — Enter a new password for your account.</p>
              </div>
              <PasswordFields
                password={password} setPassword={setPassword}
                confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
                showPwd={showPwd} setShowPwd={setShowPwd}
                onEnter={handleResetPassword}
              />
              <PrimaryButton onClick={handleResetPassword} disabled={loading || password.length < 6 || password !== confirmPassword} active={password.length >= 6 && password === confirmPassword}>
                {loading ? 'Saving...' : 'Save New Password'}
              </PrimaryButton>
            </>
          )}

          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 28, textAlign: 'center', lineHeight: 1.6 }}>
            By continuing, you agree to our{' '}
            <a href="#!" style={{ color: 'var(--blue)', textDecoration: 'none' }}>Terms</a> and{' '}
            <a href="#!" style={{ color: 'var(--blue)', textDecoration: 'none' }}>Privacy Policy</a>.
          </p>
        </div>
      </div>

      <style>{``}</style>
    </div>
  );
}

// ── Reusable sub-components ─────────────────────────────────

function PhoneInput({ value, onChange, onEnter }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>Phone Number</label>
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 700, color: '#64748b', pointerEvents: 'none' }}>+91</div>
        <input
          autoFocus
          type="tel"
          value={value}
          onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
          onKeyDown={e => e.key === 'Enter' && onEnter()}
          placeholder="10-digit mobile number"
          style={{ ...inputStyle, paddingLeft: 44 }}
        />
      </div>
    </div>
  );
}

function OtpBoxes({ otp, setOtp }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 16, width: '100%' }}>
      {[0, 1, 2, 3, 4, 5].map(i => (
        <input
          key={i}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={otp[i] || ''}
          autoFocus={i === 0}
          onChange={e => {
            const val = e.target.value.replace(/\D/g, '');
            const arr = otp.split('');
            arr[i] = val;
            const next = arr.join('').slice(0, 6);
            setOtp(next);
            if (val && i < 5) {
              const nextEl = e.target.parentNode.children[i + 1];
              if (nextEl) nextEl.focus();
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Backspace' && !otp[i] && i > 0) {
              const prevEl = e.target.parentNode.children[i - 1];
              if (prevEl) prevEl.focus();
            }
          }}
          style={{
            flex: '1 1 0', minWidth: 0, width: 0, height: 44, textAlign: 'center',
            fontSize: 18, fontWeight: 800,
            border: `2px solid ${otp[i] ? 'var(--blue)' : '#e2e8f0'}`,
            borderRadius: 8, background: '#f8fafc', color: '#0f172a',
            outline: 'none', fontFamily: 'var(--mono)', transition: 'border-color 0.15s',
            boxSizing: 'border-box',
          }}
        />
      ))}
    </div>
  );
}

function PasswordFields({ password, setPassword, confirmPassword, setConfirmPassword, showPwd, setShowPwd, onEnter }) {
  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Password</label>
        <div style={{ position: 'relative' }}>
          <input
            autoFocus
            type={showPwd ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onEnter()}
            placeholder="Min 6 characters"
            style={{ ...inputStyle, paddingRight: 44 }}
          />
          <button
            type="button"
            onClick={() => setShowPwd(v => !v)}
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#94a3b8', display: 'flex', alignItems: 'center' }}
          >
            <EyeIcon open={showPwd} />
          </button>
        </div>
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Confirm Password</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onEnter()}
          placeholder="Re-enter password"
          style={inputStyle}
        />
        {confirmPassword && password !== confirmPassword && (
          <div style={{ fontSize: 11, color: '#e11d48', marginTop: 4 }}>Passwords do not match</div>
        )}
      </div>
    </>
  );
}

function PrimaryButton({ onClick, disabled, active, children, style = {} }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%', background: active ? 'var(--blue)' : '#e2e8f0',
        color: active ? '#fff' : '#94a3b8',
        padding: '12px', borderRadius: 8, fontSize: 14, fontWeight: 700,
        border: 'none', cursor: disabled ? 'default' : 'pointer',
        boxShadow: active ? '0 4px 12px rgba(14,165,233,0.3)' : 'none',
        opacity: disabled && active ? 0.7 : 1,
        marginBottom: 12,
        ...style,
      }}
    >
      {children}
    </button>
  );
}


function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

const backBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: '#94a3b8', fontSize: 20, padding: 0, lineHeight: 1,
};

const stepTitle = {
  fontFamily: 'var(--display)', fontSize: 24, fontWeight: 700, margin: 0, color: '#0f172a',
};

const stepSubtitle = {
  fontSize: 13, color: '#64748b', margin: '0 0 24px', lineHeight: 1.6,
};

const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6,
};

const inputStyle = {
  width: '100%', padding: '11px 14px', borderRadius: 8,
  border: '1.5px solid #e2e8f0', background: '#f8fafc',
  fontFamily: 'var(--sans)', fontSize: 14, color: '#0f172a',
  outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box',
};
